import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSubscriptionWorkflowStore } from "src/store/subscriptionWorkflowStore";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { Card, CardContent } from "src/components/ui/Card";
import { Button } from "src/components/ui/Button";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage
} from "src/components/ui/Breadcrumb";
import {
  Calculator,
  ArrowRight,
  ArrowLeft,
  Home,
  Package,
  CalendarDays,
  CheckCircle2
} from "lucide-react";
import { Link } from "react-router-dom";
import { ROUTES } from "src/routes/RouteConfig";

const NewSubscriptionPricing: React.FC = () => {
  const navigate = useNavigate();
  const { currentWorkflow, selectedProduct, isLoading } = useSubscriptionWorkflowStore();
  const { setTitle, setDescription, reset } = usePageHeaderStore();

  useEffect(() => {
    setTitle("Price Calculation");
    setDescription(`Review your pricing details for the ${currentWorkflow?.product_type || "selected"} package`);
    return () => reset();
  }, [currentWorkflow?.product_type, setTitle, setDescription, reset]);

  const handleContinue = () => {
    navigate(ROUTES.STUDENT_NEW_SUBSCRIPTION_PURCHASE.path);
  };

  const handleBack = () => {
    if (currentWorkflow?.product_type === 'booster') {
      // For booster, go back to product selection
      navigate(ROUTES.STUDENT_NEW_SUBSCRIPTION.path);
    } else {
      // For custom/preparation, go back to curriculum configuration
      navigate(ROUTES.STUDENT_NEW_SUBSCRIPTION_CONFIGURE.path);
    }
  };

  const formatFeatureName = (key: string) => {
    const readableNames: Record<string, string> = {
      progress_tracking: "Progress Tracking",
      materials_included: "Materials Included",
      study_materials_included: "Materials Included",
      sessions_per_week: "Sessions Per Week"
    };

    return readableNames[key] || key.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const includedFeatures = selectedProduct?.features && typeof selectedProduct.features === 'object'
    ? Object.entries(selectedProduct.features)
        .filter(([, value]) => value !== false && value !== null && value !== undefined && value !== "")
        .map(([key, value]) => {
          const name = formatFeatureName(key);
          return typeof value === 'boolean' ? name : `${name}: ${String(value)}`;
        })
    : [];

  const featuredInclusions = includedFeatures.length > 0
    ? includedFeatures.slice(0, 3)
    : ["Progress Tracking", "Materials Included", "Sessions Per Week: 2"];

  const price = selectedProduct?.price || 0;
  const durationDays = selectedProduct?.duration_days || 1;
  const pricePerDay = price / durationDays;

  return (
    <>
      {/* Breadcrumb Navigation */}
      <div className="mb-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={ROUTES.STUDENT_DASHBOARD.path} className="flex items-center">
                  <Home className="h-4 w-4 mr-1" />
                  Dashboard
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={ROUTES.STUDENT_SUBSCRIPTIONS.path}>
                  Subscriptions
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={ROUTES.STUDENT_NEW_SUBSCRIPTION.path}>
                  New Subscription
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Review Pricing</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <div className="space-y-5">
        <Card className="max-w-3xl rounded-xl border-slate-200 shadow-sm">
          <CardContent className="p-4 md:p-5">
            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading workflow data...</p>
              </div>
            ) : selectedProduct ? (
              <div className="space-y-5">
                <div className="grid gap-4 md:grid-cols-[4rem_1fr_auto] md:items-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 md:h-16 md:w-16">
                    <Package className="h-7 w-7 text-blue-600" />
                  </div>

                  <div className="space-y-2">
                    <div>
                      <p className="mb-1 text-sm font-bold text-blue-600">
                        Selected Package
                      </p>
                      <h2 className="text-xl font-bold tracking-normal text-slate-950 md:text-2xl">
                        {selectedProduct.name}
                      </h2>
                    </div>
                    {selectedProduct.description && (
                      <p className="max-w-md text-sm leading-relaxed text-slate-500 md:text-base">
                        {selectedProduct.description}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2 md:text-right">
                    <p className="text-3xl font-bold tracking-normal text-blue-600 md:text-4xl">
                      ${price.toFixed(2)}
                    </p>
                    <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-sm font-medium text-slate-900">
                      <CalendarDays className="h-4 w-4 text-slate-500" />
                      {durationDays} days
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-5">
                  <h3 className="mb-4 text-lg font-bold tracking-normal text-slate-950">
                    Price Breakdown
                  </h3>
                  <div className="space-y-3 text-sm md:text-base">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-slate-950">Base Price</span>
                      <span className="font-medium text-slate-950">${price.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-slate-500">Duration</span>
                      <span className="font-medium text-slate-950">{durationDays} days</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-slate-500">Price per day</span>
                      <span className="font-medium text-slate-950">${pricePerDay.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-slate-200 pt-4">
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-lg font-bold text-slate-950">Total</span>
                        <span className="text-2xl font-bold tracking-normal text-green-600">${price.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-5">
                  <h3 className="mb-4 text-lg font-bold tracking-normal text-slate-950">
                    What's Included
                  </h3>
                  <div className="grid gap-3 md:grid-cols-3 md:divide-x md:divide-slate-200">
                    {featuredInclusions.map((feature) => (
                      <div key={feature} className="flex items-center gap-2 md:px-4 md:first:pl-0">
                        <CheckCircle2 className="h-5 w-5 flex-none text-green-500" />
                        <span className="text-sm font-medium text-slate-900">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Calculator className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No Product Selected
                </h3>
                <p className="text-gray-600 mb-6">
                  Please go back and select a product to see pricing details.
                </p>
                <Button onClick={() => navigate(ROUTES.STUDENT_NEW_SUBSCRIPTION.path)}>
                  Select Product
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex max-w-3xl justify-between gap-3">
          <Button
            variant="outline"
            onClick={handleBack}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          {selectedProduct && (
            <Button onClick={handleContinue}>
              Continue to Purchase
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

export default NewSubscriptionPricing;
